import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  AppNotification,
  NotificationsResponse,
} from '../models/notification.interface';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly apiUrl = environment.baseUrl + 'user/notifications';

  private notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  notifications$: Observable<AppNotification[]> =
    this.notificationsSubject.asObservable();

  private totalUnreadSubject = new BehaviorSubject<number>(0);
  totalUnread$: Observable<number> = this.totalUnreadSubject.asObservable();

  private hasErrorSubject = new BehaviorSubject<boolean>(false);
  hasError$: Observable<boolean> = this.hasErrorSubject.asObservable();

  hasNew$: Observable<boolean> = this.notifications$.pipe(
    map((list) => list.some((n) => !n.isRead))
  );

  constructor(private readonly http: HttpClient) {}

  load(): Observable<NotificationsResponse> {
    this.hasErrorSubject.next(false);
    return this.http.get<NotificationsResponse>(this.apiUrl).pipe(
      tap({
        next: (response) => {
          const list = response?.notifications?.data ?? [];
          this.notificationsSubject.next(list);
          this.totalUnreadSubject.next(
            response?.notifications?.totalUnread ??
              list.filter((n) => !n.isRead).length
          );
        },
        error: () => {
          this.hasErrorSubject.next(true);
        },
      })
    );
  }

  markAsRead(notificationId: number): Observable<unknown> {
    return this.http
      .put(`${this.apiUrl}/${notificationId}/read`, {})
      .pipe(tap(() => this.updateReadLocally(notificationId)));
  }

  markAllAsRead(): Observable<unknown> {
    return this.http
      .put(`${this.apiUrl}/read-all`, {})
      .pipe(tap(() => this.updateAllReadLocally()));
  }

  setNotifications(list: AppNotification[]): void {
    const safeList = list ?? [];
    this.notificationsSubject.next(safeList);
    this.totalUnreadSubject.next(
      safeList.filter((n) => !n.isRead).length
    );
  }

  private updateReadLocally(notificationId: number): void {
    const updated = this.notificationsSubject
      .getValue()
      .map((n) =>
        n.id === notificationId ? { ...n, isRead: true } : n
      );
    this.notificationsSubject.next(updated);
    this.totalUnreadSubject.next(
      Math.max(0, this.totalUnreadSubject.getValue() - 1)
    );
  }

  private updateAllReadLocally(): void {
    const updated = this.notificationsSubject
      .getValue()
      .map((n) => (n.isRead ? n : { ...n, isRead: true }));
    this.notificationsSubject.next(updated);
    this.totalUnreadSubject.next(0);
  }
}
